import { type Account, AccountStatus } from '../dto/account';

/**
 * Whether an account has been claimed by the firm it belongs to.
 *
 * An unclaimed account is a shell: a client invited this firm to a project, so the account exists
 * and people can be on it, but nobody at the firm has said the firm wants it. `Deferred` is the
 * status that says so, and it is the only one that does - every other status, including the `New`
 * of an account mid-signup, belongs to an account somebody has taken on.
 *
 * Written as "is it Deferred" rather than "is it one of the good ones" on purpose. A status added
 * later is far more likely to be another kind of claimed account than another kind of shell, and
 * the allow-list version would silently lock the company out of its own screens until someone
 * noticed and amended the list.
 *
 * One definition because two would drift. The claim screen asks this to decide whether there is
 * still anything to claim, and the rail asks it to decide what the company may reach - and those
 * two disagreeing is how a customer ends up being offered a claim on an account they already own.
 */
export const isAccountClaimed = (account?: Pick<Account, 'status'>): boolean =>
  !!account && account.status !== AccountStatus.Deferred;

/** The inverse, for the places that read better in the negative. */
export const isAccountUnclaimed = (account?: Pick<Account, 'status'>): boolean =>
  account?.status === AccountStatus.Deferred;
